import TwilioChatClient from './twilioChatClient'
import { debug } from '../Logger'
import EventEmitter from 'events'
import { Conversation, Message } from '@twilio/conversations'
import { captureException, addBreadcrumb } from '@sentry/browser'

class ChatManager extends EventEmitter {

  chatClientPriority = [
    TwilioChatClient
  ]
  clientIndex = 0
  chatClient: TwilioChatClient | null = null
  joinedChannels: string[] = []
  resetClientTimer = null
  token: string

  constructor (token) {

    super()
    this.token = token

  }

  static create = async (token: string): Promise<ChatManager> => {

    const chat = new ChatManager(token)

    try {

      await chat.initialize()

    }
    catch (e) {

      addBreadcrumb({
        message: 'Could not initialise the chat'
      })
      captureException(e)
      return e

    }

    return chat

  }

  createClient = async (): Promise<TwilioChatClient | undefined> => {

    if (this.chatClient) {

      this.chatClient.removeAllListeners()
      this.chatClient = null

    }

    try {

      this.chatClient = await this.chatClientPriority[this.clientIndex].create(this.token)

    }
    catch (e) {

      debug(`Failed to connect with client ${this.chatClientPriority[this.clientIndex].name} : Error: ${e.message}`)

    }

    if (!this.chatClient && this.chatClientPriority.length > this.clientIndex + 1) {

      try {

        this.forwardClient()
        await this.createClient()

      }
      catch (e) {

        return e

      }

    }

    if (!this.chatClient && this.chatClientPriority.length <= this.clientIndex + 1) {

      throw new Error('Failed to initialise a client')

    }

    if (this.chatClient) return this.chatClient
    return

  }

  forwardClient = () => {

    this.clientIndex = this.clientIndex + 1 >= this.chatClientPriority.length ? 0 : this.clientIndex + 1

  }

  resetClient = () => {

    this.clientIndex = 0

  }

  initialize = async (): Promise<void> => {

    this.resetClientTimer = null

    try {

      await this.createClient()

    }
    catch (e) {

      return e

    }

    if (!this.chatClient) return

    this.chatClient.on('disconnect', () => {

      this.emit('disconnect')

    })

    this.chatClient.on('connect', () => {

      this.emit('connect')

    })

    this.chatClient.on('messageAdded', (message) => this.emit('messageAdded', message))
    this.chatClient.on('typingStarted', (data) => this.emit('typingStarted', data))
    this.chatClient.on('typingEnded', (data) => this.emit('typingEnded', data))

    return

  }

  checkUserIsInConversation = async (channelName: string, id: string): Promise<boolean> => {

    if (!this.chatClient) return false

    try {

      const result = await this.chatClient.checkUserIsInConversation(channelName, id)
      return result

    } catch (e) {

      addBreadcrumb({
        message: 'Could not check if user was in the conversation'
      })
      captureException(e)
      return false

    }

  }

  addUserToConversation = async (channelName: string, id: string, token: string): Promise<boolean> => {

    if (!this.chatClient) return false

    try {

      const result = await this.chatClient.addUserToConversation(channelName, id, token)
      return result

    } catch (e) {

      addBreadcrumb({
        message: 'Could not add user to the conversation'
      })
      captureException(e)
      return false

    }
  }

  createNewConversation = async (channelName: string, id: string): Promise<string | null> => {

    if (!this.chatClient) return null

    try {

      const result = await this.chatClient.createNewConversation(channelName, id)
      return result

    } catch (e) {

      addBreadcrumb({
        message: 'Could not create a new conversation'
      })
      captureException(e)
      return null

    }

  }

  joinConversation = async (channelName: string): Promise<boolean | Conversation | Error> => {

    if (!this.chatClient) return false

    try {

      const result = await this.chatClient.joinConversation(channelName)
      if (result) this.joinedChannels.push(channelName)
      return result

    }
    catch (e) {

      addBreadcrumb({
        message: 'Could not join the conversation'
      })
      captureException(e)

      return e

    }

  }

  typing = (channel) => {

    if (!this.chatClient) return

    return this.chatClient.typing(channel)

  }

  sendMessage = (channel, message, messageAttributes) => {

    if (!this.chatClient) return

    return this.chatClient.sendMessage(channel, message, messageAttributes)

  }

  getMessages = (channelName, limit) => {

    if (!this.chatClient) return

    return this.chatClient.getMessages(channelName, limit)

  }

  getNumMessages = async (channelName, limit) => {

    if (!this.chatClient) return

    let messages: Message[] = []

    try {

      messages = await this.chatClient.getMessages(channelName, limit)

    }
    catch (e) {

      debug(e)

    }

    return messages.length

  }

  getNumReadMessages = async (channelName, author) => {

    if (!this.chatClient) return

    let lastMessageIndex

    try {

      lastMessageIndex = await this.chatClient.getLastReadMessageIndex(channelName, author)
      if (typeof lastMessageIndex !== 'number') lastMessageIndex = -1

    }
    catch (e) {

      debug(e)
      lastMessageIndex = -1

    }

    return lastMessageIndex + 1

  }

  setMessagesRead = (channelName, lastConsumedMessageIndex = 0) => {

    if (!this.chatClient) return
    this.chatClient.setMessagesRead(channelName, lastConsumedMessageIndex)

  }

}

export default ChatManager
